import {Box, Typography} from '@mui/material';
import {t} from 'i18next';
import {ReactComponent as InfoIcon} from './../../../assets/images/info-icon.svg';
import {useCallback, useState, useMemo, useEffect, useRef} from 'react';
import './ManageHeader.scss';
import {useSelector} from 'react-redux';
import {Trans} from 'react-i18next';
import {ISelectItem} from '../../../shared/types';
import {useCommentsViewList} from '../../../hooks/comments/useCommentsViewList';
import {RootState} from '../../../redux/store';
import {RoleNames} from '../../../shared/constants';
import ArrTooltipWrapper from '../../common/arr-tooltip-wrapper/ArrTooltipWrapper';
import ArrSingleSelectDropdown from '../../common/arr-select-dropdown/ArrSingleSelectDropdown';

export interface ManageHeaderProps {
  title: string | React.ReactNode;
  infoContentTitle: string;
  infoContent: string;
  isShowGroup?: boolean;
  onChangeGroup?: (group: ISelectItem | null) => void;
}

const ManageHeader: React.FC<ManageHeaderProps> = ({
  title,
  infoContentTitle,
  infoContent,
  isShowGroup = true,
  onChangeGroup,
}: ManageHeaderProps) => {
  const [selectedGroup, setSelectedGroup] = useState<ISelectItem | null>(null);

  const {data: commentsViewListData} = useCommentsViewList(isShowGroup);

  const loginUser = useSelector(
    (state: RootState) => state.authenticatedUser.user
  );

  const allSecurityGroups = useRef<ISelectItem>({
    id: 0,
    name: t('manageCommentFields.allSecurityGroups'),
  });

  useEffect(() => {
    if (loginUser?.securityGroupId) {
      const getSecurityGroup = commentsViewListData?.securityGroupList?.find(
        (item) => item.securityGroupID === loginUser.securityGroupId
      );
      if (getSecurityGroup) {
        setSelectedGroup({
          id: getSecurityGroup.securityGroupID,
          name: getSecurityGroup.securityGroupName,
        });
      }
    }
  }, [commentsViewListData?.securityGroupList, loginUser?.securityGroupId]);

  useEffect(() => {
    onChangeGroup?.(selectedGroup);
  }, [onChangeGroup, selectedGroup]);

  const securityGroups = useMemo(() => {
    const securityGroupList: ISelectItem[] | undefined =
      commentsViewListData?.securityGroupList?.map((group) => ({
        id: group.securityGroupID,
        name: group.securityGroupName,
      }));

    if (loginUser.roleName === RoleNames.ProjectAdministrator) {
      securityGroupList?.unshift(allSecurityGroups.current);
    }
    return securityGroupList ?? [];
  }, [commentsViewListData?.securityGroupList, loginUser.roleName]);

  useEffect(() => {
    if (loginUser.roleName === RoleNames.ProjectAdministrator) {
      setSelectedGroup(allSecurityGroups.current);
    }
  }, [loginUser.roleName]);

  const handleSelectGroup = useCallback(
    (group: ISelectItem | null) => {
      setSelectedGroup(group);
    },
    [onChangeGroup]
  );

  return (
    <Box className="manage-header" data-testid={'manage-header'}>
      <Box className="manage-title">
        <Typography
          className="typo-text headline3_size manage-title-text"
          tabIndex={0}
          aria-label={title}
          data-testid={`manage-header-title`}
        >
          {title}
        </Typography>
        <ArrTooltipWrapper
          title={infoContentTitle}
          closeTooltipOnEscPress
          trigger={
            <InfoIcon
              fontSize="medium"
              className="user-info-icon"
              data-testid="user-info-icon"
              tabIndex={0}
              aria-label={t('common.infoTooltip')}
            />
          }
        >
          <Trans
            i18nKey={infoContent}
            components={{
              br: <br data-testid={`manage-header-br`} />,
              strong: <strong data-testid={`manage-header-strong`} />,
              p: <p data-testid={`manage-header-p`} />,
              ul: <ul data-testid={`manage-header-ul`} />,
              li: <li data-testid={`manage-header-li`} />,
              h2: <h2 data-testid={`manage-header-h2`} />,
              h3: <h3 data-testid={`manage-header-h3`} />,
            }}
          >
            {infoContent}
          </Trans>
        </ArrTooltipWrapper>
      </Box>
      {isShowGroup && (
        <Box className={'manage-security-group-container'}>
          <Typography
            className="typo-text body3_size"
            tabIndex={0}
            aria-label={t('common.group')}
            data-testid={`manage-header-group`}
          >
            {t('common.group')}
          </Typography>
          <ArrSingleSelectDropdown
            dropdownLabel={''}
            dataTestId={'asd-comment-security-group-dropdown'}
            sizeVariant="medium"
            customWidth={21.3}
            dropdownItems={securityGroups}
            selectedValues={selectedGroup}
            disabled={loginUser.roleName === RoleNames.ReviewManager}
            onSelectionChange={handleSelectGroup}
            searchable
            showNoResultsText
            tabIndex={-1}
          />
        </Box>
      )}
    </Box>
  );
};

export default ManageHeader;
